import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLock, FiX } from 'react-icons/fi';
import { useGuestUpgradeStore } from '../../stores/guest-upgrade.store';
import { usePromptDialog } from '../../hooks/usePromptDialog';
import { useScrimClose } from '../../hooks/useScrimClose';
import { Z_GUEST_PROMPT } from '../../utils/layers';

/**
 * The in-page counterpart of GuestWall: same lock-icon + pitch + "Create a free
 * account" language, shown as a modal when a GUEST triggers a gated ACTION (e.g.
 * signing up for the next circle). Single global instance — mounted in app.tsx.
 */
export default function GuestUpgradeModal() {
  const { isOpen, title, description, close } = useGuestUpgradeStore();
  // A press begun in the panel and let go over the scrim keeps the dialog
  // (hooks/useScrimClose); at every width, since the scrim always shows.
  const scrim = useScrimClose({ onClose: close });
  // The dialog contract (hooks/usePromptDialog): focus in, Tab kept inside,
  // Escape and Android Back close it, focus back to whatever opened it.
  const { titleId, dialogRef } = usePromptDialog(isOpen, close);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{ zIndex: Z_GUEST_PROMPT }}
          className="fixed inset-0 flex items-center justify-center p-6 bg-(--ee-scrim)/40"
          onMouseDownCapture={scrim.onMouseDownCapture}
          onMouseUpCapture={scrim.onMouseUpCapture}
          onClick={scrim.onClick}
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.97 }}
            ref={dialogRef}
            tabIndex={-1}
            className="max-w-sm w-full text-center bg-base-100 border border-base-300 rounded-2xl p-8 relative outline-none"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
          >
            <button
              onClick={close}
              aria-label="Close"
              className="absolute top-3 right-3 text-base-content/40 hover:text-base-content"
            >
              <FiX />
            </button>
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
              <FiLock className="w-6 h-6 text-primary" />
            </div>
            <h2 id={titleId} className="text-lg font-semibold mb-2">
              {title}
            </h2>
            <p className="text-sm text-base-content/60 mb-6">{description}</p>
            <Link to="/register" onClick={close} className="btn btn-primary w-full">
              Create a free account
            </Link>
            <p className="mt-3 text-xs text-base-content/50">
              Already have one?{' '}
              <Link to="/login" onClick={close} className="text-primary hover:underline">
                Log in
              </Link>
            </p>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
